import { PVP_MAX_COMMANDS_PER_SECOND } from '@kenocyber/shared';
import type { PvpIntent } from './connection';

/**
 * Keys, pointer and wheel, reduced to the one thing a duel client may say.
 *
 * ## Why this is a module and not part of the scene
 *
 * Because every rule in it is a rule that can be wrong without anything looking
 * wrong: a key held while the window loses focus, a diagonal that quietly asks
 * for more speed than a straight line, a pointer converted with a stale camera,
 * a zoom that changes what "aiming there" means. None of those show up in a
 * screenshot, and all of them decide a duel.
 *
 * ## What it refuses to do
 *
 * - **It does not decide speed.** The wire takes `-1 | 0 | 1` per axis and the
 *   room owns how fast that is, so a diagonal cannot be made faster here even
 *   deliberately - there is no field for it.
 * - **It does not create damage.** Fire is a boolean the room reads at its own
 *   cadence. A client that spawned its own projectile would be drawing a shot
 *   the server never fired.
 * - **It does not number anything.** The sequence belongs to the connection and
 *   starts at the server's `ack + 1`. Nothing here counts.
 */

/* -------------------------------------------------------------------------- */
/*  Zoom                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * How far out a player may pull the camera.
 *
 * Named constants rather than magic numbers in a wheel handler, because the
 * bound is the whole point: an unbounded wheel is a player who can zoom until
 * the arena is four pixels wide, or until one hull fills the screen and the
 * other is off it. Neither is a view of a duel.
 *
 * Zoom is PRESENTATION and nothing else. It does not touch physics, range, aim
 * or collision - those are the room's, and the room cannot see this number.
 */
export const PVP_ZOOM_MIN = 0.4;
export const PVP_ZOOM_MAX = 1.6;
/**
 * Where the camera starts.
 *
 * Below 1 on purpose: the arena is 3.2M by 2M milli-units and a duel is decided
 * by where the other ship is, so a player who has to hunt for their opponent
 * has been given a worse view than the fight deserves.
 */
export const PVP_ZOOM_DEFAULT = 0.75;
/** One wheel notch, as a multiplier. Multiplicative so each notch feels equal. */
export const PVP_ZOOM_STEP = 1.1;

export function clampPvpZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) {
    // A wheel event that produced a nonsense delta must not blank the camera.
    return PVP_ZOOM_DEFAULT;
  }
  return Math.min(PVP_ZOOM_MAX, Math.max(PVP_ZOOM_MIN, zoom));
}

/** Applies one wheel notch. Positive `delta` (scroll down) zooms out. */
export function zoomAfterWheel(current: number, delta: number): number {
  if (delta === 0 || !Number.isFinite(delta)) {
    return clampPvpZoom(current);
  }
  return clampPvpZoom(delta > 0 ? current / PVP_ZOOM_STEP : current * PVP_ZOOM_STEP);
}

/* -------------------------------------------------------------------------- */
/*  Aim                                                                       */
/* -------------------------------------------------------------------------- */

/*
 * **A projecção da mira mudou-se para `@kenocyber/shared`** (gate V28I.BW.5C).
 *
 * Ela descreve o contrato da mira, e passou a ter um segundo consumidor: o
 * piloto determinístico da bancada de integração, que tem de apontar como este
 * cliente aponta. Duas cópias divergiriam; esta reexporta a única que existe.
 */
export { pvpAimVector } from '@kenocyber/shared';

/* -------------------------------------------------------------------------- */
/*  Movement and fire                                                         */
/* -------------------------------------------------------------------------- */

/** The keys a duel reads. Nothing else on the keyboard does anything. */
const MOVEMENT_KEYS = {
  KeyW: { axis: 'y', value: -1 },
  KeyS: { axis: 'y', value: 1 },
  KeyA: { axis: 'x', value: -1 },
  KeyD: { axis: 'x', value: 1 },
} as const;

export type PvpMovementKey = keyof typeof MOVEMENT_KEYS;

export function isPvpMovementKey(code: string): code is PvpMovementKey {
  return code in MOVEMENT_KEYS;
}

/**
 * As duas componentes de movimento que este conjunto de teclas produz.
 *
 * Extraída para ser chamada por **duas** fontes de intenção — a do duelo e a do
 * mundo aberto — em vez de a segunda voltar a escrever o mapeamento. Um segundo
 * mapeamento seria a tecla `W` a significar coisas diferentes em dois modos no
 * dia em que um deles mudasse.
 *
 * Somar em vez de escolher é deliberado: `A` e `D` ao mesmo tempo dão zero, que
 * é o que os dedos estão a pedir.
 */
export function pvpMovementAxes(held: ReadonlySet<string>): {
  readonly mx: -1 | 0 | 1;
  readonly my: -1 | 0 | 1;
} {
  let mx = 0;
  let my = 0;
  for (const code of held) {
    if (!isPvpMovementKey(code)) {
      continue;
    }
    const key = MOVEMENT_KEYS[code];
    if (key.axis === 'x') {
      mx += key.value;
    } else {
      my += key.value;
    }
  }
  return {
    mx: Math.sign(mx) as -1 | 0 | 1,
    my: Math.sign(my) as -1 | 0 | 1,
  };
}

/** Axes at zero, fire released, aim unchanged. What a cleared intent means. */
/**
 * The key that ARMS the teleport, and only arms it.
 *
 * Space, because it is the one key a player already has a spare finger on
 * while holding WASD and a mouse button. It is not a movement key and never
 * enters the held set.
 *
 * Pressing it does not teleport. It opens a target mode, and the next click
 * chooses where - which is the whole difference between this and the dash it
 * replaced. Escape closes the mode without spending anything.
 */
export const PVP_TELEPORT_KEY = 'Space';

/** Closes the target mode without choosing. */
export const PVP_CANCEL_KEY = 'Escape';

/**
 * **A tecla que DETONA as marcas de sangramento** (gate V28G.N).
 *
 * `Digit1`, e é o código FÍSICO e não o carácter: num teclado AZERTY o `1` está
 * atrás de shift, e `event.key` daria `&`. `event.code` é a tecla ONDE ela está,
 * que é o que um jogador carrega.
 *
 * ## Ela estava livre, e isso foi verificado
 *
 * O duelo lê WASD, `Space` e `Escape`, e mais nada - a caça não lê teclado
 * nenhum. Nenhum dígito estava atribuído, portanto isto não rouba uma tecla a
 * ninguém: o §4 do contrato proibia inventar um atalho SOBRE um já ocupado, e
 * este não o está.
 *
 * O botão continua a existir e a ser a via principal. Uma tecla é um atalho para
 * quem já tem a mão no teclado, exatamente como `Space` é para o teleporte.
 */
export const PVP_DETONATE_KEY = 'Digit1';

export const PVP_IDLE_INTENT: PvpIntent = {
  mx: 0,
  my: 0,
  ax: 0,
  ay: 0,
  firing: false,
  // A cleared source has no pending choice, and this constant is what a cleared
  // source looks like - so it says so rather than leaving the field out.
  teleportTo: null,
  detonateBleed: false,
  /* E nenhuma carta pedida: um intent limpo não pediu nada (gate V28H.J). */
  activateCard: null,
  boardSummon: null,
  /* Nem nenhuma carga solta (gate V28H.N). */
  releaseCard: null,
};

/**
 * The player's current intent, assembled from whatever is held down.
 *
 * Deliberately a small state machine rather than a read of Phaser's key
 * objects: the cases that matter - a window blurring with W held, a tab hidden
 * mid-charge, a match ending while the mouse is down - are all about keys that
 * are still "down" as far as the browser is concerned and must stop counting
 * anyway.
 */
export class PvpIntentSource {
  private readonly held = new Set<PvpMovementKey>();
  private firing = false;
  private aim: { ax: number; ay: number } = { ax: 0, ay: 0 };
  /**
   * Whether the player is currently choosing where to go.
   *
   * Presentation and input state, not a claim: being in the mode grants
   * nothing, and leaving it costs nothing. The room never hears about it - what
   * it eventually hears is one point, once.
   */
  private targeting = false;
  /**
   * The point the player picked, waiting for the next command frame.
   *
   * Survives exactly one `current()`. A click must produce one request: if it
   * stayed set, every frame for the rest of the duel would ask again, and the
   * room would answer the first and refuse the rest - or, with a second charge,
   * spend it.
   */
  private teleportTo: { x: number; y: number } | null = null;

  /**
   * A ordem de DETONAR à espera do próximo frame de comando (gate V28G.N).
   *
   * Sobrevive a exatamente um , pela mesma razão exata que o ponto do
   * teleporte sobrevive: uma pressão tem de produzir UM pedido. Se ficasse
   * armada, cada frame do resto do duelo voltaria a pedir - e a sala responderia
   * ao primeiro e faria `no-op` a todos os outros, o que é inofensivo e é
   * mentira sobre o que o jogador fez.
   *
   * Não há modo a armar, ao contrário do teleporte: uma detonação não escolhe
   * um sítio. É uma ordem, e ela é dada no instante em que o botão é premido.
   */
  private detonateBleed = false;

  /**
   * **A ORDEM de ligar uma carta, à espera de partir** (gate V28H.J).
   *
   * Um trinco como a detonação, e pela mesma razão: uma ordem vale um envio. O
   * espaço e a identidade da ordem viajam juntos - a identidade é gerada no
   * instante em que o jogador pede, e repetida se o pedido for reenviado.
   *
   * Uma segunda ordem antes de a primeira partir SUBSTITUI-A: um trinco guarda
   * uma, e a última é a que o jogador quis. Duas ordens no mesmo frame seriam
   * uma delas silenciosamente perdida.
   */
  private activateCard: { readonly slot: 1 | 2 | 3; readonly commandId: string } | null = null;
  private boardSummon: { readonly commandId: string } | null = null;
  /** O latch da SOLTURA, separado: um toque liga e solta no mesmo quadro. */
  private releaseCard: { readonly slot: 1 | 2 | 3; readonly commandId: string } | null = null;

  press(code: string): void {
    if (isPvpMovementKey(code)) {
      this.held.add(code);
    }
    if (code === PVP_TELEPORT_KEY) {
      // Arms, never fires. The click is what chooses.
      this.targeting = true;
    }
    if (code === PVP_CANCEL_KEY) {
      this.targeting = false;
    }
    /*
     * A ordem de DETONAR, armada pela tecla exatamente como o botão a arma.
     *
     * `press` só é chamado quando `event.repeat` é falso - a cena verifica-o -
     * portanto manter a tecla carregada dá UMA ordem, e não uma por quadro.
     */
    if (code === PVP_DETONATE_KEY) {
      this.requestDetonateBleed();
    }
  }

  /** Whether the arena should be drawing a target mode right now. */
  isTargeting(): boolean {
    return this.targeting;
  }

  /** Opened by the HUD button, which is the primary way in. */
  armTeleport(): void {
    this.targeting = true;
  }

  /**
   * Pede uma DETONAÇÃO, para o próximo frame de comando (gate V28G.N).
   *
   * Premida no botão do HUD, que é a única forma: o §4 do contrato proíbe roubar
   * uma tecla já ocupada, e este gate não inventa um sistema de atalhos livres.
   *
   * Não concede nada e não decide nada. Quem tem marcas, quantas e sobre quem é
   * uma pergunta que a sala responde; isto só diz que o jogador carregou.
   */
  requestDetonateBleed(): void {
    this.detonateBleed = true;
  }

  /**
   * **O jogador pediu para ligar a carta deste espaço** (gate V28H.J).
   *
   * Não concede nada e não decide nada: quem tem carta, se ela pode ligar e por
   * quanto tempo é uma pergunta que a SALA responde. Isto só diz que o jogador
   * carregou - a mesma forma da detonação, e pela mesma razão.
   *
   * A identidade da ordem nasce aqui, e é ela que torna uma repetição segura do
   * outro lado.
   */
  requestCardActivation(slot: 1 | 2 | 3, commandId: string): void {
    this.activateCard = { slot, commandId };
  }

  /**
   * **O jogador soltou a carga deste espaço** (gate V28H.N).
   *
   * O gémeo de {@link requestCardActivation}, e com o seu próprio latch: um
   * toque rápido liga e solta dentro do mesmo quadro, e um latch partilhado
   * perderia uma das duas ordens - o toque viraria uma carga por soltar.
   */
  requestCardRelease(slot: 1 | 2 | 3, commandId: string): void {
    this.releaseCard = { slot, commandId };
  }

  /**
   * **O jogador carregou em PILOTAR** (gate V28H.X).
   *
   * Não diz em quê: a sala escolhe a estrutura que este casco está a tocar, e
   * este lado não tem onde nomear outra. Latch próprio, como as outras duas
   * ordens - embarcar não é ligar nem soltar.
   */
  requestBoard(commandId: string): void {
    this.boardSummon = { commandId };
  }

  /** Closed by Escape, by a choice, by a blur, or by the charge running out. */
  cancelTeleport(): void {
    this.targeting = false;
  }

  /**
   * Takes the click, if the player was choosing.
   *
   * Answers whether the click was CONSUMED, so the caller knows not to treat it
   * as a shot. Firing on the same click that picks a destination would make the
   * act of teleporting also an attack, which is not what the player asked for.
   */
  chooseTeleport(point: { readonly x: number; readonly y: number }): boolean {
    if (!this.targeting) {
      return false;
    }
    this.targeting = false;
    this.teleportTo = { x: Math.round(point.x), y: Math.round(point.y) };
    return true;
  }

  release(code: string): void {
    if (isPvpMovementKey(code)) {
      this.held.delete(code);
    }
  }

  setFiring(firing: boolean): void {
    this.firing = firing;
  }

  /** Takes a fresh aim, or keeps the last one when the pointer is on the hull. */
  aimAt(vector: { readonly ax: number; readonly ay: number } | null): void {
    if (vector) {
      this.aim = { ax: vector.ax, ay: vector.ay };
    }
  }

  /**
   * Forgets everything held.
   *
   * The answer to blur, to a hidden tab, to a socket dropping and to a match
   * ending. Every one of them leaves a key "down" in the browser's opinion and
   * would otherwise leave a ship flying into a wall while its player is looking
   * at something else - and, on a lost socket, would send that direction the
   * instant the connection came back.
   *
   * The AIM is kept. A cannon pointing where the player last pointed it is
   * correct; a cannon snapping to a default because a window lost focus is not.
   */
  clear(): void {
    this.held.clear();
    this.firing = false;
    /*
     * A choice made as the window was losing focus is a choice nobody meant,
     * and it costs a charge - so it is dropped rather than delivered late. The
     * MODE closes with it: coming back to a still-armed cursor after a blur is
     * how a player teleports somewhere they were not looking at.
     */
    this.teleportTo = null;
    this.targeting = false;
    /* E a ordem da carta cai com o resto, pela mesma razão (gate V28H.J). */
    this.activateCard = null;
    /*
     * E a ordem de detonar cai com o resto, pela mesma razão: uma ordem dada
     * enquanto a janela perdia o foco é uma ordem que ninguém deu.
     */
    this.detonateBleed = false;
  }

  current(): PvpIntent {
    /*
     * Opposite keys cancel, and a diagonal is just two axes at one.
     *
     * There is no length here to normalise: the wire takes three values per
     * axis, and how fast that is - and that a diagonal is not faster than a
     * straight line - is the room's decision, made in the domain where both
     * sides can see it.
     */
    let mx = 0;
    let my = 0;
    for (const code of this.held) {
      const key = MOVEMENT_KEYS[code];
      if (key.axis === 'x') {
        mx += key.value;
      } else {
        my += key.value;
      }
    }
    /*
     * Read once and cleared, so a click produces exactly one request.
     *
     * `current()` is called both to send a command and to draw a frame, which
     * is why this has to be consumed HERE rather than after a send: the one
     * caller that consumes it is the one that will put it on the wire, and a
     * second reader would either double the request or swallow it.
     */
    const teleportTo = this.teleportTo;
    this.teleportTo = null;
    /* Lida uma vez e limpa, pela mesma razão exata (gate V28G.N). */
    const detonateBleed = this.detonateBleed;
    this.detonateBleed = false;
    /* E a ordem da carta, pela mesma razão exata (gate V28H.J). */
    const activateCard = this.activateCard;
    this.activateCard = null;
    /* E a soltura, pela mesma razão exacta (gate V28H.N). */
    const releaseCard = this.releaseCard;
    this.releaseCard = null;
    /* E a de pilotar, pela mesma razão exacta (gate V28H.X). */
    const boardSummon = this.boardSummon;
    this.boardSummon = null;
    return {
      mx: sign(mx),
      my: sign(my),
      ax: this.aim.ax,
      ay: this.aim.ay,
      firing: this.firing,
      teleportTo,
      detonateBleed,
      activateCard,
      releaseCard,
      boardSummon,
    };
  }

  /** What is held, WITHOUT consuming the pending choice. For drawing only. */
  peek(): PvpIntent {
    const held = this.current();
    // Put it back: this reader is a renderer, and a renderer must not spend
    // something the network layer has not sent yet.
    this.teleportTo = held.teleportTo ?? null;
    /* E a ordem da carta volta, pela mesma razão (gate V28H.J). */
    this.activateCard = held.activateCard ?? null;
    /* E a soltura volta, pela mesma razão (gate V28H.N). */
    this.releaseCard = held.releaseCard ?? null;
    /* E a de pilotar volta: um renderer não gasta o que a rede não enviou. */
    this.boardSummon = held.boardSummon ?? null;
    /* E a ordem também volta: um renderer não gasta o que a rede não enviou. */
    this.detonateBleed = held.detonateBleed === true;
    return { ...held, teleportTo: null, detonateBleed: false };
  }
}

function sign(value: number): -1 | 0 | 1 {
  return value > 0 ? 1 : value < 0 ? -1 : 0;
}

/* -------------------------------------------------------------------------- */
/*  Cadence                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * How often intent goes out.
 *
 * One per tick, and not one per input event. A browser produces pointer moves
 * far faster than 20 Hz, and the room drops anything past its per-second
 * allowance and closes a socket that keeps at it - so a client that published
 * every raw event would be rate-limiting itself out of its own duel.
 *
 * 50 ms rather than something faster because the room applies at most one
 * command per tick anyway: a second command inside the same tick can only
 * overwrite the first.
 */
export const PVP_INTENT_INTERVAL_MS = 50;

/**
 * Commands per second this cadence actually produces, at the interval above.
 *
 * Exported so a test can compare it against the room's ceiling rather than
 * against a number copied into an assertion: if either constant moves, the
 * relationship is what has to keep holding.
 */
export const PVP_INTENT_RATE_PER_SECOND = Math.ceil(1_000 / PVP_INTENT_INTERVAL_MS);

/** True while the cadence stays inside what the room will accept. */
export const pvpCadenceFitsAllowance = (): boolean =>
  PVP_INTENT_RATE_PER_SECOND <= PVP_MAX_COMMANDS_PER_SECOND;

/**
 * Decides when to publish: once per tick, always, while the scene is running.
 *
 * ## Why an unchanged intent still goes out
 *
 * It used to be held back for {@link PVP_INTENT_INTERVAL_MS} on a change and
 * for a 250 ms heartbeat otherwise, on the reasoning that repeating yourself on
 * the wire is waste. It is not waste here, because **the room's clock is the
 * message stream**: the duel is driven by incoming commands rather than by a
 * timer (see `room-object.ts`, "What drives the clock"), so a client that says
 * nothing is not a quiet client, it is a stopped room.
 *
 * That is what the player was seeing. Holding a key produces exactly one
 * changed intent; for the next 250 ms the client said nothing, the room did not
 * advance, and then one command arrived and the accumulator resolved five ticks
 * at once into a single broadcast frame. Measured in a real duel: authoritative
 * frames arriving every 214 ms carrying four to six ticks each, 100% of the
 * time, against a prediction moving smoothly every 28 ms. Every one of those
 * lumps was a correction, and the large ones crossed the snap threshold and
 * teleported the ship.
 *
 * So the interval is the whole rule. Twenty commands a second against the
 * room's allowance of {@link PVP_MAX_COMMANDS_PER_SECOND} = 90 - the same
 * bound this cadence was already written to respect, and the same 50 ms the
 * room ticks at, so each message advances exactly one tick.
 */
export class PvpIntentCadence {
  private lastSentAt = Number.NEGATIVE_INFINITY;

  /**
   * **Whether a publication is due now.**
   *
   * ## O defeito que separar isto de `next` corrigiu (gate V28G.N)
   *
   * A cena chamava `next(controls.current(), now)`. `current()` CONSOME as
   * ordens de uma pressão só - detonar, teleportar - e só depois é que a
   * cadência decidia se publicava. Num quadro recusado, a ordem era lida,
   * apagada e deitada fora: a sessenta quadros por segundo com um intervalo de
   * 50 ms, dois em cada três cliques desapareciam sem chegar a existir.
   *
   * O jogador via um botão que "às vezes não faz nada" e culpava a rede. Não era
   * a rede: a ordem nunca saiu desta máquina.
   *
   * Perguntar ANTES de ler é o que garante que uma pressão lida é uma pressão
   * publicada. Ela pode esperar até um intervalo pelo próximo quadro devido -
   * cinquenta milissegundos no pior caso - e nunca mais se perde.
   */
  due(now: number): boolean {
    return now - this.lastSentAt >= PVP_INTENT_INTERVAL_MS;
  }

  /** Regista que uma publicação saiu agora. Chamado DEPOIS de a intenção sair. */
  mark(now: number): void {
    this.lastSentAt = now;
  }

  /** The intent to publish now, or `null` to say nothing this frame. */
  next(intent: PvpIntent, now: number): PvpIntent | null {
    if (!this.due(now)) {
      return null;
    }
    this.mark(now);
    return intent;
  }

  /** Forgets the last publication, so the next intent goes out immediately. */
  reset(): void {
    this.lastSentAt = Number.NEGATIVE_INFINITY;
  }
}
